import { canonical, distanceKm, SAME_PLACE_KM, type Coords } from '../geolocation'
import { nativeStoreSync } from '../native'
import { read, readJson, remove, write } from '../storage'

/**
 * Saved places and the default location (Phase 9 plan §6). Per device, in
 * `localStorage`, read synchronously by `loadPrefs()` because the boot needs
 * `openTo` before anything renders.
 *
 * Everything here is validate-or-drop: an entry that fails is dropped rather
 * than repaired, the same rule `loadPrefs` applies to layer ids. The list can
 * also arrive from a link somebody sent, which is untrusted input, so the link
 * parser is stricter still and refuses a malformed link whole.
 *
 * This module knows nothing about the selection. Switching to a place is
 * `here.svelte.ts`'s, beside the boot that also selects one.
 */

const KEY_PLACES = 'weather.places'
const KEY_OPEN_TO = 'weather.openTo'

/** Twenty is a lot of places to scroll past in a switcher, and a short link. */
export const MAX_PLACES = 20
export const MAX_NAME = 40
/** An NWS place name ("8 mi from Lawrence, KS") is well under this. */
const MAX_LABEL = 80

export interface SavedPlace {
  /** Random, from `crypto.getRandomValues`: `randomUUID` needs a secure context. */
  id: string
  /** What the user sees. Editable, at most 40 characters. */
  name: string
  /** 4 decimal places, like every selection. */
  lat: number
  lon: number
  /** The name NWS gave the point when it was saved, kept beside a custom name. */
  label?: string
  zip?: string
  /** Push alerts opted in: the bell on its Settings row (Stage 9.6). */
  alerts?: boolean
}

/** `'here'` or a saved place's id. */
export type OpenTo = 'here' | string

export const places = $state({
  list: [] as SavedPlace[],
  openTo: 'here' as OpenTo,
  /**
   * `navigator.storage.persisted()`, once asked: whether the browser has
   * promised not to evict this origin's storage. null until known, and on a
   * browser without the Storage API.
   */
  persisted: null as boolean | null,
  /**
   * A short line for the status line: "Saved Home", "20 places is the
   * limit". Cleared after a few seconds; it is about a thing just done.
   */
  message: null as string | null,
  /** A place whose Settings row should open and take focus (the filled star). */
  focus: null as string | null,
  /** Places from a `#places=` link opened at boot, waiting for Add or Ignore. */
  offer: null as SharedPlace[] | null,
})

const ID_RE = /^[A-Za-z0-9_-]{8,32}$/
const ZIP_RE = /^\d{5}$/

function validLatLon(lat: unknown, lon: unknown): boolean {
  return (
    typeof lat === 'number' &&
    typeof lon === 'number' &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  )
}

function validName(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0 && v.length <= MAX_NAME
}

function validPlace(v: unknown): v is SavedPlace {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false
  const p = v as SavedPlace
  return (
    typeof p.id === 'string' &&
    ID_RE.test(p.id) &&
    validName(p.name) &&
    validLatLon(p.lat, p.lon) &&
    (p.label === undefined || (typeof p.label === 'string' && p.label.length <= MAX_LABEL)) &&
    (p.zip === undefined || (typeof p.zip === 'string' && ZIP_RE.test(p.zip))) &&
    (p.alerts === undefined || typeof p.alerts === 'boolean')
  )
}

/** Copy only the known fields, so nothing else stored under the key survives. */
function clean(p: SavedPlace): SavedPlace {
  const out: SavedPlace = { id: p.id, name: p.name.trim(), lat: canonical(p.lat), lon: canonical(p.lon) }
  if (p.label !== undefined) out.label = p.label
  if (p.zip !== undefined) out.zip = p.zip
  if (p.alerts !== undefined) out.alerts = p.alerts
  return out
}

/** Read once at boot, from `loadPrefs()`. */
export function loadPlaces(): void {
  const stored = readJson<unknown>(KEY_PLACES)
  const seen = new Set<string>()
  const list: SavedPlace[] = []
  for (const entry of Array.isArray(stored) ? stored : []) {
    if (!validPlace(entry) || seen.has(entry.id)) continue
    seen.add(entry.id)
    list.push(clean(entry))
    if (list.length === MAX_PLACES) break
  }
  places.list = list
  // A default that names a place no longer saved reads as My location
  // (decision 1). It is not rewritten: the next explicit choice does that.
  const openTo = read(KEY_OPEN_TO)
  places.openTo = openTo && seen.has(openTo) ? openTo : 'here'
  places.message = null
  places.focus = null
  places.offer = null
  // Every launch, so a list saved before the widgets existed reaches them.
  syncNative()
}

function persistList(): void {
  write(KEY_PLACES, JSON.stringify(places.list))
  syncNative()
}

/** The iOS app's copy for its widgets (a no-op on the web). */
function syncNative(): void {
  nativeStoreSync(places.list, places.openTo)
}

export function getPlace(id: string | undefined): SavedPlace | null {
  if (!id) return null
  return places.list.find((p) => p.id === id) ?? null
}

/**
 * The saved place `point` *is*: the one it was selected as, else the nearest
 * within 1 km (decision 10). One forecast cell cannot tell them apart, so a
 * tap beside Home is Home, and saving it again adds nothing.
 */
export function savedFor(point: (Coords & { place?: string }) | null): SavedPlace | null {
  if (!point) return null
  const byId = point.place && point.place !== 'here' ? getPlace(point.place) : null
  if (byId) return byId
  return nearestSaved(point, places.list)
}

function nearestSaved<T extends Coords>(point: Coords, list: T[]): T | null {
  let best: T | null = null
  let bestKm = SAME_PLACE_KM
  for (const p of list) {
    const km = distanceKm(point, p)
    if (km < bestKm) {
      best = p
      bestKm = km
    }
  }
  return best
}

export function newPlaceId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(9))
  return toBase64Url(bytes)
}

export type SaveResult =
  | { kind: 'saved'; place: SavedPlace }
  | { kind: 'exists'; place: SavedPlace }
  | { kind: 'full' }

/**
 * Save a point. Within 1 km of a saved place it *is* that place, and nothing
 * is added. The 21st says so in the status line rather than failing quietly.
 */
export function savePlace(point: Coords & { name: string; label?: string; zip?: string }): SaveResult {
  const existing = savedFor(point)
  if (existing) return { kind: 'exists', place: existing }
  if (places.list.length >= MAX_PLACES) {
    say(`${MAX_PLACES} places is the limit`)
    return { kind: 'full' }
  }
  const place: SavedPlace = {
    id: newPlaceId(),
    name: clampName(point.name) || 'Saved place',
    lat: canonical(point.lat),
    lon: canonical(point.lon),
  }
  if (point.label && point.label.length <= MAX_LABEL) place.label = point.label
  if (point.zip && ZIP_RE.test(point.zip)) place.zip = point.zip
  places.list = [...places.list, place]
  persistList()
  say(`Saved ${place.name}`)
  void askPersist()
  return { kind: 'saved', place }
}

function clampName(name: string): string {
  return name.trim().slice(0, MAX_NAME).trim()
}

/** An empty name keeps the old one: a place must be called something. */
export function renamePlace(id: string, name: string): void {
  const value = clampName(name)
  if (!value) return
  places.list = places.list.map((p) => (p.id === id ? { ...p, name: value } : p))
  persistList()
}

/** Move a place one step up (`-1`) or down (`1`) the list. */
export function movePlace(id: string, delta: -1 | 1): void {
  const from = places.list.findIndex((p) => p.id === id)
  const to = from + delta
  if (from < 0 || to < 0 || to >= places.list.length) return
  const list = [...places.list]
  ;[list[from], list[to]] = [list[to], list[from]]
  places.list = list
  persistList()
}

/** Move a place to a position, for a drag (Settings' grip). Clamped. */
export function movePlaceTo(id: string, index: number): void {
  const from = places.list.findIndex((p) => p.id === id)
  if (from < 0) return
  const to = Math.max(0, Math.min(places.list.length - 1, Math.round(index)))
  if (to === from) return
  const list = [...places.list]
  const [moved] = list.splice(from, 1)
  list.splice(to, 0, moved)
  places.list = list
  persistList()
}

/**
 * Remove a place. Removing the default puts the default back to My location,
 * and the return value says it happened so Settings can say so.
 */
export function removePlace(id: string): { wasDefault: boolean } {
  places.list = places.list.filter((p) => p.id !== id)
  persistList()
  if (places.focus === id) places.focus = null
  const wasDefault = places.openTo === id
  if (wasDefault) setOpenTo('here')
  return { wasDefault }
}

/**
 * The bell on a place's row. Only the list changes here; telling the server
 * is `push.svelte.ts`'s, which is also what decides whether a bell may be
 * turned on at all (permission, a subscription).
 */
export function setPlaceAlerts(id: string, on: boolean): void {
  places.list = places.list.map((p) => {
    if (p.id !== id) return p
    const { alerts: _, ...rest } = p
    return on ? { ...rest, alerts: true } : rest
  })
  persistList()
}

/** The places whose bell is on, in the list's order: the first one names an alert. */
export function alertPlaces(): SavedPlace[] {
  return places.list.filter((p) => p.alerts)
}

export function setOpenTo(value: OpenTo): void {
  places.openTo = value === 'here' || getPlace(value) ? value : 'here'
  if (places.openTo === 'here') remove(KEY_OPEN_TO)
  else write(KEY_OPEN_TO, places.openTo)
  syncNative()
}

/** The filled star: open that place's row in Settings. */
export function focusPlace(id: string): void {
  places.focus = id
}

let messageTimer: ReturnType<typeof setTimeout> | undefined
const MESSAGE_MS = 6000

/**
 * Clear the line early: it describes the point that was on screen, and a
 * switch to another place makes "Saved Lawrence, KS" read as if it were
 * about Home.
 */
export function unsay(): void {
  clearTimeout(messageTimer)
  places.message = null
}

/** Show `text` in the status line for a few seconds. */
export function say(text: string): void {
  places.message = text
  clearTimeout(messageTimer)
  messageTimer = setTimeout(() => (places.message = null), MESSAGE_MS)
}

/**
 * Ask the browser to keep this origin's storage (§6.6), and ignore the
 * answer: Chromium decides by engagement and Safari by whether the app is
 * installed, and neither asks the user. What Settings shows is `persisted()`,
 * read afterwards, because that is what is actually true.
 */
export async function askPersist(): Promise<void> {
  try {
    await navigator.storage?.persist?.()
  } catch {
    // Not offered, or refused. Settings says which.
  }
  await readPersisted()
}

export async function readPersisted(): Promise<void> {
  try {
    const value = await navigator.storage?.persisted?.()
    places.persisted = typeof value === 'boolean' ? value : null
  } catch {
    places.persisted = null
  }
}

// ---------------------------------------------------------------------------
// Moving places to another device (§6.7)
// ---------------------------------------------------------------------------

/** A place as a link carries it: no id, which the receiving device makes. */
export interface SharedPlace {
  name: string
  lat: number
  lon: number
  label?: string
  zip?: string
}

type Tuple = [string, number, number] | [string, number, number, string | null] | [string, number, number, string | null, string]

/**
 * `<origin>/#places=<base64url JSON>`. A hash rather than a query so the list
 * never reaches a server log; each place is a tuple rather than an object so
 * twenty of them stay around 1.5 KB.
 */
export function placesLink(list: readonly SavedPlace[], origin: string): string {
  const tuples: Tuple[] = list.map((p) => {
    if (p.zip) return [p.name, p.lat, p.lon, p.label ?? null, p.zip]
    if (p.label) return [p.name, p.lat, p.lon, p.label]
    return [p.name, p.lat, p.lon]
  })
  const bytes = new TextEncoder().encode(JSON.stringify(tuples))
  return `${origin}/#places=${toBase64Url(bytes)}`
}

/** Longer than twenty places could ever encode to; anything bigger is refused unread. */
const MAX_LINK_CHARS = 8000
const LINK_RE = /(?:^|#)places=([A-Za-z0-9_-]+)$/

/**
 * The places in a link, a pasted URL, or just its `#places=…` part; null for
 * anything else, with a warning. Strict throughout: numbers in range, names
 * of at most 40 characters, at most twenty entries, and one bad entry refuses
 * the whole link, the way a malformed deep link is ignored whole.
 */
export function parsePlacesLink(text: string): SharedPlace[] | null {
  const trimmed = text.trim()
  if (trimmed.length > MAX_LINK_CHARS) return refuse('it is too long')
  const match = LINK_RE.exec(trimmed)
  if (!match) return refuse('there is no #places= in it')
  let raw: unknown
  try {
    raw = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(fromBase64Url(match[1])))
  } catch {
    return refuse('it does not decode')
  }
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_PLACES) {
    return refuse('it is not a list of up to twenty places')
  }
  const out: SharedPlace[] = []
  for (const entry of raw) {
    const place = sharedPlace(entry)
    if (!place) return refuse('an entry is malformed')
    out.push(place)
  }
  return out
}

function sharedPlace(v: unknown): SharedPlace | null {
  if (!Array.isArray(v) || v.length < 3 || v.length > 5) return null
  const [name, lat, lon, label, zip] = v as unknown[]
  if (!validName(name) || !validLatLon(lat, lon)) return null
  const place: SharedPlace = { name: name.trim(), lat: canonical(lat as number), lon: canonical(lon as number) }
  if (label !== undefined && label !== null) {
    if (typeof label !== 'string' || label.length > MAX_LABEL) return null
    place.label = label
  }
  if (zip !== undefined) {
    if (typeof zip !== 'string' || !ZIP_RE.test(zip)) return null
    place.zip = zip
  }
  return place
}

function refuse(why: string): null {
  console.warn(`ignoring a places link: ${why}`)
  return null
}

/** The places in `incoming` that are not already saved, nor repeats of each other. */
export function newPlaces(incoming: readonly SharedPlace[]): SharedPlace[] {
  const fresh: SharedPlace[] = []
  for (const p of incoming) {
    if (nearestSaved(p, places.list) || nearestSaved(p, fresh)) continue
    fresh.push(p)
  }
  return fresh
}

export interface MergeResult {
  added: number
  /** Already saved, within 1 km. */
  known: number
  /** New, but past the cap. */
  dropped: number
}

/** Add what a link carries to the list. Never replaces it. */
export function mergePlaces(incoming: readonly SharedPlace[]): MergeResult {
  const fresh = newPlaces(incoming)
  const room = Math.max(0, MAX_PLACES - places.list.length)
  const taken = fresh.slice(0, room)
  if (taken.length) {
    places.list = [
      ...places.list,
      ...taken.map((p) => ({ ...p, id: newPlaceId() })),
    ]
    persistList()
    void askPersist()
  }
  return { added: taken.length, known: incoming.length - fresh.length, dropped: fresh.length - taken.length }
}

/** What a merge did, in words. */
export function mergeText(result: MergeResult): string {
  const parts: string[] = []
  if (result.added) parts.push(`Added ${result.added} place${result.added === 1 ? '' : 's'}`)
  if (result.known) parts.push(`${result.known} already saved`)
  if (result.dropped) parts.push(`${result.dropped} left out: ${MAX_PLACES} places is the limit`)
  return parts.join(' · ') || 'Nothing to add'
}

/**
 * Read a `#places=` link out of the address bar at boot, then clear the hash
 * so a reload does not offer it again. What it carries waits in
 * `places.offer` for the user's Add or Ignore; nothing is saved unasked.
 */
export function consumePlacesLink(): void {
  const url = new URL(window.location.href)
  if (!url.hash.startsWith('#places=')) return
  const incoming = parsePlacesLink(url.hash)
  try {
    history.replaceState(history.state, '', `${url.pathname}${url.search}`)
  } catch {
    // A sandboxed frame can refuse it. The link has still been read once.
  }
  if (!incoming) return
  const fresh = newPlaces(incoming)
  if (fresh.length === 0) say('Those places are already saved')
  else places.offer = incoming
}

/**
 * Offer a link opened while the app is already running. Pasting one into the
 * address bar of an open tab, or following one from another page of the same
 * origin, changes only the hash: no reload, so no boot to read it. Returns a
 * disposer.
 */
export function watchPlacesLinks(): () => void {
  const onHash = () => consumePlacesLink()
  window.addEventListener('hashchange', onHash)
  return () => window.removeEventListener('hashchange', onHash)
}

/** Add the offered places, or only the ones ticked in the offer. */
export function acceptOffer(only?: readonly SharedPlace[]): void {
  if (!places.offer) return
  say(mergeText(mergePlaces(only ?? places.offer)))
  places.offer = null
}

export function dismissOffer(): void {
  places.offer = null
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(text: string): Uint8Array {
  const b64 = text.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
  return Uint8Array.from(binary, (c) => c.charCodeAt(0))
}

/** Tests only. */
export function _resetPlaces(): void {
  clearTimeout(messageTimer)
  places.list = []
  places.openTo = 'here'
  places.persisted = null
  places.message = null
  places.focus = null
  places.offer = null
}
